import { Injectable, PLATFORM_ID, REQUEST, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';

export interface SocialShareTags {
  /** Page title — also used for og:title / twitter:title */
  title: string;
  /** Plain text or HTML (will be stripped) — used for the preview snippet */
  description: string;
  /** Absolute or site-relative image URL. Falls back to the site logo. */
  image?: string;
  /** Site-relative path of the canonical page, e.g. `/post/123` */
  path: string;
  /** og:type — defaults to 'website' (use 'article' for blog posts) */
  type?: string;
}

const SITE_NAME = 'Safeword';
const DEFAULT_IMAGE_PATH = '/images/logo/Safeword-icon.svg';
/** Only used if neither window nor the SSR request is available (should not happen in practice). */
const FALLBACK_ORIGIN = 'https://safeword.com';

/**
 * Populates Open Graph / Twitter Card meta tags so links shared to
 * Facebook, WhatsApp, etc. render a rich preview (image, title, site name)
 * instead of a bare link — mirrors what sites like Eventbrite show.
 *
 * Must run during SSR (post/event/blog detail routes are RenderMode.Server
 * in app.routes.server.ts) since share-preview crawlers fetch the URL once
 * and read the initial HTML — they don't execute client-side navigation.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private meta = inject(Meta);
  private titleService = inject(Title);
  private platformId = inject(PLATFORM_ID);
  private request = inject(REQUEST, { optional: true });

  private get origin(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.origin;
    }
    if (this.request) {
      try { return new URL(this.request.url).origin; } catch { /* fall through */ }
    }
    return FALLBACK_ORIGIN;
  }

  setSocialTags(tags: SocialShareTags): void {
    const url = `${this.origin}${tags.path}`;
    const image = this.absolutize(tags.image || DEFAULT_IMAGE_PATH);
    const title = this.truncate(tags.title, 70) || SITE_NAME;
    const description = this.truncate(tags.description, 200) || `View this on ${SITE_NAME}`;

    this.titleService.setTitle(`${title} | ${SITE_NAME}`);

    this.setTag('og:title', title, 'property');
    this.setTag('og:description', description, 'property');
    this.setTag('og:image', image, 'property');
    this.setTag('og:url', url, 'property');
    this.setTag('og:type', tags.type ?? 'website', 'property');
    this.setTag('og:site_name', SITE_NAME, 'property');

    this.setTag('twitter:card', 'summary_large_image', 'name');
    this.setTag('twitter:title', title, 'name');
    this.setTag('twitter:description', description, 'name');
    this.setTag('twitter:image', image, 'name');
  }

  private absolutize(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    return `${this.origin}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  private truncate(text: string, max: number): string {
    const plain = (text ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return plain.length > max ? plain.slice(0, max - 1) + '…' : plain;
  }

  private setTag(name: string, content: string, attr: 'property' | 'name'): void {
    if (!content) return;
    if (this.meta.getTag(`${attr}="${name}"`)) {
      this.meta.updateTag({ [attr]: name, content });
    } else {
      this.meta.addTag({ [attr]: name, content });
    }
  }
}
